import React from "react";
import { Link } from "react-router-dom";
import EmailRegistrationModal from "../forms/EmailRegistrationModal";
import useEmailRegistration from "../../hooks/useEmailRegistration";

/**
 * Footer component - appears at the bottom of all pages
 */
export default function Footer() {
  const { showRegistrationModal, openRegistrationModal, closeRegistrationModal } = useEmailRegistration();

  return (
    <footer className="bg-[#FFF5EB] py-12 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Logo + CTA */}
          <div className="flex flex-col items-center justify-center">
            <div className="flex items-center mb-4">
              <span className="text-2xl font-bold">Hiree</span>
            </div>
            <Link 
              to="/signup"
              className="px-6 py-3 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90 mb-4">
              Register now!
            </Link>
            
            {/* Social Media Icons */}
            <div className="flex space-x-4 mt-4 justify-center">
              {/* Instagram */}
              <a href="https://www.instagram.com/hiree.us?igsh=MXUwMnM4bTY3cTB1cw%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#FF8C00] transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </a>
              
              {/* Twitter/X */}
              <a href="https://x.com/hireeus?s=21" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#FF8C00] transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              
              {/* LinkedIn */}
              <a href="https://www.linkedin.com/company/hiree-us" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#FF8C00] transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                </svg>
              </a>
              
              {/* TikTok */}
              <a href="https://www.tiktok.com/@hiree.us?_t=ZP-8wPhUrYa22x&_r=1" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#FF8C00] transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16.6 5.82s.51.5 0 0A4.278 4.278 0 0 1 15.54 3h-3.09v12.4a2.592 2.592 0 0 1-2.59 2.5c-1.42 0-2.59-1.16-2.59-2.5 0-1.4 1.16-2.5 2.59-2.5.33 0 .64.08.93.2v-3.15c-.3-.06-.6-.1-.93-.1-3.41 0-6.09 2.61-6.09 5.95 0 3.34 2.7 5.95 6.09 5.95 3.41 0 6.09-2.61 6.09-5.95V8.9c1.4 1.13 3.3 1.82 5.45 1.82v-3.1c-1.69 0-3.2-.67-4.3-1.8z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Product Column */}
          <div>
            <h3 className="font-bold mb-4">Product</h3>
            <div className="space-y-2 text-gray-600">
              <p>Pricing</p>
              <p>Sign In</p>
              <p>Documentation</p>
            </div>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="font-bold mb-4">Company</h3>
            <div className="space-y-2 text-gray-600">
              <Link to="/about" className="block hover:text-[#FF8C00]">About Us</Link>
              <Link to="/blog" className="block hover:text-[#FF8C00]">Blog</Link>
              <p>Careers</p>
              <Link to="/contact" className="block hover:text-[#FF8C00]">Contact Us</Link>
            </div>
          </div>

          {/* Legal Column */}
          <div>
            <h3 className="font-bold mb-4">Legal</h3>
            <div className="space-y-2 text-gray-600">
              <Link to="/terms" className="block hover:text-[#FF8C00]">Terms & Conditions</Link>
              <Link to="/privacy" className="block hover:text-[#FF8C00]">Privacy Policy</Link>
              {/* <p>Sitemap</p> */}
            </div>
          </div>
        </div>

        <div className="text-center text-gray-600 text-sm">
          © 2025, All Rights Reserved by Hiree Inc.
        </div>
      </div>
      
      {/* Registration Modal */}
      <EmailRegistrationModal 
        show={showRegistrationModal} 
        onClose={closeRegistrationModal} 
      />
    </footer>
  );
}
